import cx from "classnames";
import { FC, useId, useMemo, useState } from "react";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesColors } from "@app/shared/qes-standards/colors";
import { MultiMapExportStateGroup } from "@app/projects/network-assessment/multi-map-export/interfaces";
import { formatCountyCount, formatServiceAreaSummary, stateCheckboxState } from "@app/projects/network-assessment/multi-map-export/utils/serviceAreaSelection";

export interface CountiesSelectorProps {
  /** Separates an empty filter from an empty project. */
  hasCounties: boolean;
  onChange: (regionSetIds: number[]) => void;
  selectedCountyRegionSetIds: readonly number[];
  /** Only the states, and counties within them, that match the selected County Status pills. */
  statesInScope: readonly MultiMapExportStateGroup[];
}

export const CountiesSelector: FC<CountiesSelectorProps> = ({
  hasCounties,
  onChange,
  selectedCountyRegionSetIds,
  statesInScope,
}: CountiesSelectorProps) => {
  const [expandedStates, setExpandedStates] = useState<readonly string[]>([]);
  const baseId = useId();

  const countiesInView = useMemo(() => statesInScope.flatMap((state) => state.counties), [statesInScope]);
  const selected = useMemo(() => new Set(selectedCountyRegionSetIds), [selectedCountyRegionSetIds]);
  const selectedInView = countiesInView.filter((county) => selected.has(county.regionSetId));
  const allInViewSelected = countiesInView.length > 0 && selectedInView.length === countiesInView.length;

  const setCountiesSelected = (regionSetIds: readonly number[], isSelected: boolean) => {
    const changed = new Set(regionSetIds);

    onChange(isSelected
      ? [...selectedCountyRegionSetIds, ...regionSetIds.filter((regionSetId) => !selected.has(regionSetId))]
      : selectedCountyRegionSetIds.filter((regionSetId) => !changed.has(regionSetId)));
  };

  const toggleExpanded = (stateAbbreviation: string) => setExpandedStates((current) =>
    current.includes(stateAbbreviation)
      ? current.filter((expanded) => expanded !== stateAbbreviation)
      : [...current, stateAbbreviation]);

  const showRequiredError = countiesInView.length > 0 && selectedInView.length === 0;

  return (
    <div className="multi-map-export__field">
      <span className="multi-map-export__field-label" data-testid="multi-map-export__counties-label">
        Counties<span className="text-danger"> *</span>
      </span>
      <div
        className={cx({
          "multi-map-export__counties": true,
          "multi-map-export__counties--error": showRequiredError,
        })}
        data-testid="multi-map-export__counties"
      >
        <div className="multi-map-export__counties-summary-bar">
          {countiesInView.length > 0 && (
            <QESCheckbox
              checked={allInViewSelected}
              containerClassName="multi-map-export__counties-select-all"
              // Native indeterminate leaves checked false, so a partial selection clicks through to Select All.
              indeterminate={selectedInView.length > 0 && !allInViewSelected}
              label="Select All"
              onChange={(event) => setCountiesSelected(countiesInView.map((county) => county.regionSetId), event.target.checked)}
              testId="multi-map-export__counties-select-all"
            />
          )}
          <span className="multi-map-export__counties-summary" data-testid="multi-map-export__counties-summary">
            {formatServiceAreaSummary(statesInScope)}
          </span>
        </div>
        {countiesInView.length === 0 ? (
          <p className="multi-map-export__counties-empty" data-testid="multi-map-export__counties-empty">
            {hasCounties ? "No counties match the selected statuses." : "This project has no counties."}
          </p>
        ) : (
          <ul aria-label="Counties" className="multi-map-export__counties-list">
            {statesInScope.map((state) => {
              const isExpanded = expandedStates.includes(state.stateAbbreviation);
              const checkboxState = stateCheckboxState(state.counties, selectedCountyRegionSetIds);
              const panelId = `${baseId}-${state.stateAbbreviation}`;

              return (
                <li key={state.stateAbbreviation}>
                  <div className="multi-map-export__counties-row multi-map-export__counties-row--state">
                    <button
                      aria-controls={panelId}
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? "Collapse" : "Expand"} ${state.stateName}`}
                      className={cx({
                        "multi-map-export__counties-expander": true,
                        "multi-map-export__counties-expander--expanded": isExpanded,
                      })}
                      data-testid={`multi-map-export__counties-expander--${state.stateAbbreviation}`}
                      onClick={() => toggleExpanded(state.stateAbbreviation)}
                      type="button"
                    >
                      <QesIcon color={QesColors.QesPrimaryGray8} iconName={QesIcons.ChevronDown} isDecorative={true} width="14" />
                    </button>
                    <QESCheckbox
                      checked={checkboxState === CheckboxStateEnum.Checked}
                      containerClassName="multi-map-export__counties-checkbox"
                      indeterminate={checkboxState === CheckboxStateEnum.Indeterminate}
                      label={state.stateName}
                      onChange={(event) => setCountiesSelected(state.counties.map((county) => county.regionSetId), event.target.checked)}
                      testId={`multi-map-export__counties-state--${state.stateAbbreviation}`}
                    />
                    <span className="multi-map-export__counties-row-meta">{formatCountyCount(state.counties.length)}</span>
                  </div>
                  {isExpanded && (
                    <ul className="multi-map-export__counties-county-list" id={panelId}>
                      {state.counties.map((county) => (
                        <li className="multi-map-export__counties-row multi-map-export__counties-row--county" key={county.regionSetId}>
                          <QESCheckbox
                            checked={selected.has(county.regionSetId)}
                            containerClassName="multi-map-export__counties-checkbox"
                            label={county.name}
                            onChange={(event) => setCountiesSelected([county.regionSetId], event.target.checked)}
                            testId={`multi-map-export__counties-county--${county.regionSetId}`}
                          />
                          <span className="multi-map-export__counties-row-meta">{county.status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {showRequiredError && (
        <p className="multi-map-export__field-error" data-testid="multi-map-export__counties-error">Select at least one county.</p>
      )}
    </div>
  );
};

export default CountiesSelector;
